import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    Box,
    Typography
} from '@mui/material';
import { StaffService } from '../api/services/staffService';

const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const parts = timeStr.split(':');
    if (parts.length >= 2) {
        const hh = parts[0].trim().padStart(2, '0');
        const mm = parts[1].trim().padStart(2, '0');
        return `${hh}:${mm}`;
    }
    return timeStr;
};


export default function ShiftFormModal({ open, onClose, onSuccess, shiftData }) {
    const isEditMode = !!shiftData;
    const [formData, setFormData] = useState({
        name: '',
        startTime: '08:00',
        endTime: '16:00',
        departmentId: ''
    });
    const [departments, setDepartments] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingDepts, setIsLoadingDepts] = useState(false);
    const [error, setError] = useState('');

    const fetchDepartments = async () => {
        setIsLoadingDepts(true);
        try {
            const data = await StaffService.getDepartments();
            setDepartments(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch departments:', err);
        } finally {
            setIsLoadingDepts(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchDepartments();
            if (shiftData) {
                setFormData({
                    name: shiftData.name || '',
                    startTime: formatTime(shiftData.startTime) || '08:00',
                    endTime: formatTime(shiftData.endTime) || '16:00',
                    departmentId: shiftData.departmentId || shiftData.department?.id || ''
                });
            } else {
                setFormData({
                    name: '',
                    startTime: '08:00',
                    endTime: '16:00',
                    departmentId: ''
                });
            }
            setError('');
        }
    }, [open, shiftData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');

        try {
            if (isEditMode) {
                // PATCH: Only send changed fields
                const updates = {};
                
                const formattedStartTime = formatTime(formData.startTime);
                const formattedEndTime = formatTime(formData.endTime);
                const originalStartTime = formatTime(shiftData.startTime);
                const originalEndTime = formatTime(shiftData.endTime);

                if (formData.name.trim() !== (shiftData.name || '').trim()) {
                    updates.name = formData.name.trim();
                }
                if (formattedStartTime !== originalStartTime) {
                    updates.startTime = formattedStartTime;
                }
                if (formattedEndTime !== originalEndTime) {
                    updates.endTime = formattedEndTime;
                }
                
                const currentDeptId = shiftData.departmentId || shiftData.department?.id || '';
                if (formData.departmentId !== currentDeptId) {
                    updates.departmentId = formData.departmentId;
                }

                if (Object.keys(updates).length === 0) {
                    onClose();
                    return;
                }

                await StaffService.updateShift(shiftData.id || shiftData._id, updates);
                onSuccess('Shift updated successfully');
            } else {
                // POST: Full creation
                if (!formData.name || !formData.startTime || !formData.endTime || !formData.departmentId) {
                    throw new Error('All fields are required.');
                }

                const postData = {
                    name: formData.name.trim(),
                    startTime: formatTime(formData.startTime),
                    endTime: formatTime(formData.endTime),
                    departmentId: formData.departmentId
                };

                await StaffService.createShift(postData);
                onSuccess('Shift template created successfully');
            }
            onClose();
        } catch (err) {
            if (err.response?.status === 403 || err.response?.status === 405) {
                setError('Action not allowed while the hotel is suspended.');
            } else {
                setError(err.message || 'Failed to save shift template.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontWeight: 'bold' }}>
                {isEditMode ? 'Update Shift Template' : 'Create New Shift Template'}
            </DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent dividers>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    
                    <Grid container spacing={2.5}>
                        <Grid item xs={12}>
                            <TextField
                                label="Shift Name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                fullWidth
                                required
                                size="small"
                                placeholder="e.g., Morning Shift"
                                autoFocus
                            />
                        </Grid>
                        
                        <Grid item xs={6}>
                            <TextField
                                label="Start Time"
                                name="startTime"
                                type="time"
                                value={formData.startTime}
                                onChange={handleChange}
                                fullWidth
                                required
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        
                        <Grid item xs={6}>
                            <TextField
                                label="End Time"
                                name="endTime"
                                type="time"
                                value={formData.endTime}
                                onChange={handleChange}
                                fullWidth
                                required
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        
                        <Grid item xs={12}>
                            <FormControl fullWidth size="small" required>
                                <InputLabel>Assign Department</InputLabel>
                                <Select
                                    name="departmentId"
                                    value={formData.departmentId}
                                    onChange={handleChange}
                                    label="Assign Department"
                                >
                                    {isLoadingDepts ? (
                                        <MenuItem disabled><CircularProgress size={20} sx={{ mr: 1 }} /> Loading departments...</MenuItem>
                                    ) : (
                                        departments.map(dept => (
                                            <MenuItem key={dept.id || dept._id} value={dept.id || dept._id}>{dept.name}</MenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                                * All times are in 24-hour format (HH:mm).
                            </Typography>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2.5, bgcolor: 'grey.50' }}>
                    <Button onClick={onClose} color="inherit">Cancel</Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting}
                        startIcon={isSubmitting && <CircularProgress size={16} />}
                        sx={{ px: 4 }}
                    >
                        {isSubmitting ? 'Saving...' : (isEditMode ? 'Update Shift' : 'Create Shift')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}
